import { Laptop } from 'lucide-react';
import { useEffect, useState } from 'react';

import { ButtonLink } from './ui';

type Release = { version: string | null; releaseDate: string | null; diskImage: string | null };

/**
 * Cairn for Mac, offered in a browser's Settings once a release is on this
 * server's update feed (`server/desktop-releases.ts`). The disk image is behind
 * the same session as everything else, so the link works only signed in.
 * The app itself never shows this card. See docs/desktop-app.md.
 */
export function DesktopDownload() {
  const [release, setRelease] = useState<Release | null>(null);

  useEffect(() => {
    void fetch('/api/desktop/release')
      .then(response => (response.ok ? response.json() : null))
      .then(setRelease)
      .catch(() => setRelease(null));
  }, []);

  if (!release?.version || !release.diskImage) {
    return null;
  }

  const published = release.releaseDate
    ? new Date(release.releaseDate).toLocaleDateString([], {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  return (
    <section className="detail-card">
      <h2>
        <Laptop size={18} aria-hidden="true" /> Cairn for Mac
      </h2>
      <p className="muted">
        Version {release.version}
        {published ? `, published ${published}` : ''}. The same workspace in its own window, with a
        capture panel on ⌃⌥Space, the running session in the menu bar and reminders as Mac
        notifications. It updates itself from this server.
      </p>
      <div className="button-row wrap">
        <ButtonLink
          variant="secondary"
          href={`/api/desktop/updates/${encodeURIComponent(release.diskImage)}`}
          reloadDocument
        >
          Download for Mac
        </ButtonLink>
      </div>
    </section>
  );
}
